// Adding / removing ONE content item to / from several regular broadcasts at once, worked out
// as a set of multi-path updates (the caller writes them with `updateMany`, ONE atomic write).
// Nothing here touches Firebase.

import { planBroadcastUpdates } from "lib/broadcast"
import { isCompositeBroadcast } from "lib/playlist"

export const FIELDS = ["name", "showChyron", "resolutions", "contentList", "broadcastList", "duration", "updated_at"]

// The way a field is stored: no list and an empty list are the same, an unset chyron is "off"
export const stored = (field, value) => {
  if (field === "showChyron") return !!value
  if (value === undefined || (Array.isArray(value) && value.length === 0)) return null
  return value
}

export const sameField = (field, a, b) => {
  const x = stored(field, a)
  const y = stored(field, b)
  if (Array.isArray(x) && Array.isArray(y)) return x.length === y.length && x.every((value, i) => value === y[i])
  if (typeof x === "number" && typeof y === "number") return Math.abs(x - y) < 1e-6
  return x === y
}

// Adds the content item to the END of some regular broadcasts and removes it (every time it
// appears) from others. Work it out from fresh data (`getAll`), not from cached lists.
//  - broadcasts / contentItems: all of the client's, each with its `id`
//  - add / remove: ids of regular broadcasts. One that already holds the content is not added
//    again, one that does not hold it is left alone; composites and multiplier copies are skipped.
// Everything that depends on the changed broadcasts follows (the same code as when a broadcast is
// saved): total duration, the multiplier copies of those broadcasts, the composite broadcasts
// built on them. Only fields that really changed are written, and `updated_at` moves only on
// the broadcasts that changed.
// Returns { updates, added, removed }: `updates` are multi-path updates ("broadcast/<id>/<field>"),
// `added` / `removed` the ids of the broadcasts that really changed.
export const planContentMembership = ({ broadcasts, contentItems, contentId, add = [], remove = [], now = new Date().toISOString() }) => {
  const working = new Map(broadcasts.map(b => [b.id, { ...b }]))
  const added = []
  const removed = []

  const regular = (id) => {
    const record = working.get(id)
    return record && !record.sourceId && !isCompositeBroadcast(record) ? record : null
  }

  // Saves a new list for one broadcast, and the effects of that on the others
  const change = (id, contentList) => {
    const edited = { ...working.get(id), contentList, updated_at: now }
    const plan = planBroadcastUpdates({ broadcasts: [...working.values()], contentItems, edited, now })
    working.set(id, plan.edited)
    for (const [path, value] of Object.entries(plan.updates)) {
      const [, otherId, field] = path.split("/")
      const other = working.get(otherId)
      if (!other) continue
      if (value === null) delete other[field]
      else other[field] = value
    }
  }

  for (const id of new Set(remove)) {
    const record = regular(id)
    if (record && (record.contentList || []).includes(contentId)) {
      change(id, record.contentList.filter(item => item !== contentId))
      removed.push(id)
    }
  }
  for (const id of new Set(add)) {
    const record = regular(id)
    if (record && !(record.contentList || []).includes(contentId)) {
      change(id, [...(record.contentList || []), contentId])
      added.push(id)
    }
  }

  // What differs from the data that came in, field by field
  const updates = {}
  for (const original of broadcasts) {
    const next = working.get(original.id)
    for (const field of FIELDS) {
      if (!sameField(field, original[field], next[field])) {
        updates[`broadcast/${original.id}/${field}`] = stored(field, next[field])
      }
    }
  }
  return { updates, added, removed }
}
